import { motion } from 'framer-motion';
import { Globe, CheckCircle2, Minus } from 'lucide-react';
import { ConfidenceBar } from '@/components/profiler/ConfidenceBar';
import type { DossierSectionProps } from './types';
import type { IdentifierVariant } from '@/types/profiler';

// ── platform color map ────────────────────────────────────────────────────────

const PLATFORM_COLORS: Record<string, string> = {
  twitter: '#1DA1F2',
  x: '#1DA1F2',
  instagram: '#E1306C',
  facebook: '#1877F2',
  linkedin: '#0A66C2',
  github: '#6E40C9',
  telegram: '#26A5E4',
  reddit: '#FF4500',
  snapchat: '#FFFC00',
  tiktok: '#010101',
  youtube: '#FF0000',
  pinterest: '#E60023',
  discord: '#5865F2',
  whatsapp: '#25D366',
};

function platformColor(platform: string): string {
  return PLATFORM_COLORS[platform.toLowerCase()] ?? '#5A7A9A';
}

// ── main component ─────────────────────────────────────────────────────────────

export function Digital({ report, pipeline }: DossierSectionProps) {
  const cluster = pipeline?.cluster ?? report.identifier_cluster;
  const socialAccounts = pipeline?.social?.accounts ?? [];
  const usernameVariants: IdentifierVariant[] = cluster?.variants.filter(v => v.type === 'USERNAME') ?? [];
  const domainVariants: IdentifierVariant[] = cluster?.variants.filter(v => v.type === 'DOMAIN') ?? [];

  const hasAny = socialAccounts.length > 0 || usernameVariants.length > 0 || domainVariants.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
      className="flex flex-col gap-5"
    >
      {!hasAny && (
        <div
          style={{
            background: '#0C1118',
            border: '1px solid #182030',
            borderRadius: 8,
            padding: '48px 24px',
            textAlign: 'center',
          }}
        >
          <Globe size={28} style={{ color: '#283A50', margin: '0 auto 10px' }} />
          <p style={{ color: '#283A50', fontSize: 11, fontFamily: 'monospace' }}>
            No digital footprint data available
          </p>
          <p style={{ color: '#182030', fontSize: 9, fontFamily: 'monospace', marginTop: 4 }}>
            Run Social Discovery module to enumerate accounts
          </p>
        </div>
      )}

      {/* Social accounts */}
      {socialAccounts.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Globe size={12} style={{ color: '#00C8FF' }} />
            <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.15em' }}>
              SOCIAL ACCOUNTS ({socialAccounts.length})
            </p>
          </div>
          <div className="flex flex-col gap-2">
            {socialAccounts.map((acc, i) => {
              const col = platformColor(acc.platform);
              return (
                <div
                  key={i}
                  style={{
                    background: '#0C1118',
                    border: '1px solid #182030',
                    borderLeft: `3px solid ${col}`,
                    borderRadius: 8,
                    padding: '10px 12px',
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span
                        style={{
                          background: `${col}18`,
                          color: col,
                          border: `1px solid ${col}40`,
                          fontSize: 9,
                          fontFamily: 'monospace',
                          fontWeight: 700,
                          padding: '2px 7px',
                          borderRadius: 99,
                        }}
                      >
                        {acc.platform.toUpperCase()}
                      </span>
                      {acc.username && (
                        <span style={{ color: '#DCE8F5', fontSize: 11, fontFamily: 'monospace' }}>
                          @{acc.username}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      {acc.confirmed ? (
                        <span className="flex items-center gap-1 text-[9px] font-mono font-bold"
                          style={{ color: '#00C853' }}>
                          <CheckCircle2 size={10} /> CONFIRMED
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-[9px] font-mono"
                          style={{ color: '#283A50' }}>
                          <Minus size={10} /> UNCONFIRMED
                        </span>
                      )}
                    </div>
                  </div>
                  {acc.email_used && (
                    <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', marginBottom: 4 }}>
                      Via: {acc.email_used}
                    </p>
                  )}
                  {acc.url && (
                    <a
                      href={acc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: col, fontSize: 9, fontFamily: 'monospace', textDecoration: 'underline', display: 'block', marginBottom: 4 }}
                    >
                      {acc.url}
                    </a>
                  )}
                  {acc.confidence !== undefined && (
                    <ConfidenceBar value={acc.confidence} color={col} height={3} showLabel />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Username handles */}
      {usernameVariants.length > 0 && (
        <div>
          <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.15em', marginBottom: 10 }}>
            USERNAME HANDLES ({usernameVariants.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {usernameVariants.map((v, i) => (
              <div
                key={i}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
                style={{
                  background: '#0C1118',
                  border: '1px solid #182030',
                }}
              >
                <span style={{ color: '#00C8FF', fontSize: 12, fontFamily: 'monospace', fontWeight: 700 }}>
                  @{v.value}
                </span>
                <span style={{ color: '#283A50', fontSize: 8, fontFamily: 'monospace' }}>
                  {v.source}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Domain records */}
      {domainVariants.length > 0 && (
        <div>
          <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.15em', marginBottom: 10 }}>
            DOMAIN RECORDS ({domainVariants.length})
          </p>
          <div className="flex flex-col gap-2">
            {domainVariants.map((v, i) => (
              <div
                key={i}
                style={{
                  background: '#0C1118',
                  border: '1px solid #182030',
                  borderLeft: '3px solid #2979FF',
                  borderRadius: 8,
                  padding: '8px 12px',
                }}
              >
                <div className="flex items-center justify-between">
                  <span style={{ color: '#2979FF', fontFamily: 'monospace', fontSize: 11, fontWeight: 700 }}>
                    {v.value}
                  </span>
                  <div className="flex items-center gap-2" style={{ width: 80 }}>
                    <ConfidenceBar value={v.confidence} color="#2979FF" height={3} showLabel />
                  </div>
                </div>
                <span style={{ color: '#283A50', fontSize: 8, fontFamily: 'monospace', marginTop: 2 }}>
                  {v.source}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}
